// src/utils/patientFilterUtils.js
//
// Pure filter predicates and configuration for the dashboard quick-filter chips.
//
// Two kinds of chip live here:
//
// 1. Journey chips (JOURNEY_CHIP_KEYS) — mutually exclusive stages of the
//    patient assessment journey, ordered left-to-right. Each predicate captures
//    patients at exactly one stage so badge counts never double-count:
//
//      Not yet logged in → Needs clinician → Forms incomplete →
//      Forms complete → Report ready
//
// 2. Orthogonal ops chips — triage filters that may overlap journey stages in
//    meaning (a patient can match both "Needs MDT clinician" and "Forms
//    incomplete"). The UI is still single-select (only one chip active at a
//    time); orthogonality means we do NOT force journey chips to exclude these
//    patients from their counts. Today the only ops chip is needsMdtClinician
//    (Well Life Clinic only — see getVisibleChipKeys).
//
// Visibility is org-gated via getVisibleChipKeys so the dashboard does not
// hard-code which chips to strip.
//
// Data source notes:
//   - `hasSetOwnPassword` is the reliable "first login" proxy (see earlier comments
//     in git history for rationale vs. `lastLogin`).
//   - `progressByStage.preQ` tracks patient questionnaire completion as 0-100.
//     It is undefined on brand-new optimistic summaries before the backend has
//     computed it, so predicates guard against that.
//   - `reportsProgress.pr.status` is computed by `compute_reports_progress` in
//     patient_summary_functions.py. Values: 'not_started' | 'generated' | 'approved'.
//   - "Forms complete" means the patient's own pre-assessment questionnaires only.
//     Informant questionnaire completion is not tracked on patient_summaries.
//   - "Report ready" means the Patient Report (PR) specifically — the final
//     deliverable shared with patients. Other report stages (CI, SSI, DD, PAD)
//     are tracked but not surfaced as chips.
//   - DD / MDT supporting clinicians live on
//     `clinicians.clinicianDD.supportingClinicianIds` (present on summaries and
//     optimistic stubs; may be [] or missing on partial objects).

import { orgRequiresClinicianAtSetup } from '../pages/providers/2_dashboard/config/patientSetupConfig';

/**
 * Does this patient summary have a lead clinician assigned?
 * Same truthiness the journey chips already use for "has clinician".
 */
export const hasLeadClinician = (patient) =>
  !!patient?.clinicians?.clinicianLead?.clinicianId;

/**
 * Does this patient have at least one DD / MDT supporting clinician?
 *
 * Treats missing clinicianDD, missing supportingClinicianIds, [], and arrays
 * of only falsy entries as "no supporting clinician" so lead-only WLC patients
 * (and optimistic stubs with supportingClinicianIds: []) match reliably.
 */
export const hasMdtSupportingClinician = (patient) => {
  const ids = patient?.clinicians?.clinicianDD?.supportingClinicianIds;
  return Array.isArray(ids) && ids.filter(Boolean).length > 0;
};

/**
 * Quick-filter chip definitions.
 *
 * Each entry has:
 *   key          – stable identifier used as state value and in CHIP_FILTER_ORDER
 *   label        – human-readable chip label shown in the UI
 *   predicate    – pure function (patient => boolean) for filtering
 *   contextNote  – function (filteredCount, totalCount) => string for the note below the table
 *   emptyMessage – copy shown when the filter yields zero results
 */
export const CHIP_FILTERS = {
  notLoggedIn: {
    key: 'notLoggedIn',
    label: 'Not yet logged in',
    predicate: (patient) =>
      patient.hasSetOwnPassword !== true,
    contextNote: (filtered, total) =>
      `Showing ${filtered} of ${total} patient${total !== 1 ? 's' : ''} who have not yet logged in.`,
    emptyMessage: 'All patients have logged in. No action needed.',
  },
  needsClinician: {
    key: 'needsClinician',
    label: 'Needs clinician assigned',
    predicate: (patient) =>
      patient.hasSetOwnPassword === true &&
      !hasLeadClinician(patient),
    contextNote: (filtered, total) =>
      `Showing ${filtered} of ${total} patient${total !== 1 ? 's' : ''} awaiting clinician assignment.`,
    emptyMessage: 'All patients currently have a lead clinician assigned. No action needed.',
  },
  // Orthogonal ops chip (WLC only — see getVisibleChipKeys). Intentionally does
  // NOT require login and does NOT exclude patients from journey chip counts.
  needsMdtClinician: {
    key: 'needsMdtClinician',
    label: 'Needs MDT clinician',
    predicate: (patient) =>
      hasLeadClinician(patient) &&
      !hasMdtSupportingClinician(patient),
    contextNote: (filtered, total) =>
      `Showing ${filtered} of ${total} patient${total !== 1 ? 's' : ''} who have a lead clinician but no MDT (supporting) clinician assigned yet.`,
    emptyMessage: 'Every patient with a lead clinician also has an MDT clinician assigned.',
  },
  needsForms: {
    key: 'needsForms',
    label: 'Forms incomplete',
    predicate: (patient) =>
      patient.hasSetOwnPassword === true &&
      hasLeadClinician(patient) &&
      patient.progressByStage?.preQ !== undefined &&
      patient.progressByStage.preQ < 100,
    contextNote: (filtered, total) =>
      `Showing ${filtered} of ${total} patient${total !== 1 ? 's' : ''} with incomplete pre-assessment forms.`,
    emptyMessage: 'All patients have completed their forms. No action needed.',
  },
  formsComplete: {
    key: 'formsComplete',
    label: 'Forms complete',
    predicate: (patient) =>
      patient.hasSetOwnPassword === true &&
      hasLeadClinician(patient) &&
      patient.progressByStage?.preQ === 100 &&
      patient.reportsProgress?.pr?.status !== 'approved',
    contextNote: (filtered, total) =>
      `Showing ${filtered} of ${total} patient${total !== 1 ? 's' : ''} who have completed all pre-assessment forms.`,
    emptyMessage: 'No patients have completed all forms yet.',
  },
  reportReady: {
    key: 'reportReady',
    label: 'Report ready',
    predicate: (patient) =>
      patient.hasSetOwnPassword === true &&
      hasLeadClinician(patient) &&
      patient.reportsProgress?.pr?.status === 'approved',
    contextNote: (filtered, total) =>
      `Showing ${filtered} of ${total} patient${total !== 1 ? 's' : ''} with an approved Patient Report ready for download.`,
    emptyMessage: 'No patients have an approved Patient Report yet.',
  },
};

/**
 * Journey chips only — mutually exclusive pipeline. Used by exclusivity tests
 * so adding orthogonal ops chips does not break the "at most one journey chip"
 * invariant.
 */
export const JOURNEY_CHIP_KEYS = [
  'notLoggedIn',
  'needsClinician',
  'needsForms',
  'formsComplete',
  'reportReady',
];

/**
 * Full left-to-right UI order, including orthogonal ops chips.
 * needsMdtClinician sits after needsClinician. Well Life Clinic shows both
 * chips: needsClinician for patients awaiting initial assignment,
 * needsMdtClinician for patients with a lead but no MDT supporting clinician.
 */
export const CHIP_FILTER_ORDER = [
  'notLoggedIn',
  'needsClinician',
  'needsMdtClinician',
  'needsForms',
  'formsComplete',
  'reportReady',
];

/**
 * Which chip keys to show for a given organisation.
 *
 * Rules:
 *   - needsClinician: hidden once we know the org requires a lead at patient
 *     setup (most orgs). Deferred-assignment orgs (The Owl Centre, SLaM,
 *     Well Life Clinic) keep the chip so unassigned patients can be found.
 *     When organisation is still unknown/null, keep the full journey set
 *     including needsClinician — same as the previous dashboard behaviour
 *     before org data loads.
 *   - needsMdtClinician: Well Life Clinic only. Hidden for every other org
 *     and while organisation is unknown (avoids flashing an irrelevant ops
 *     chip during load). Intentionally NOT gated on ddStyle === mdtReview —
 *     other MDT orgs (TOC, LMP, AAA, etc.) are out of scope for this chip.
 *
 * @param {string|null|undefined} organisation
 * @returns {string[]}
 */
export const getVisibleChipKeys = (organisation) => {
  let keys = CHIP_FILTER_ORDER;

  // Only strip needsClinician once we know the org. Unknown org → keep it
  // (preserves the pre-load journey chip set).
  if (organisation && orgRequiresClinicianAtSetup(organisation)) {
    keys = keys.filter((key) => key !== 'needsClinician');
  }

  // Ops chip is Well Life Clinic only.
  // Hide until org is confirmed so the chip does not flash for other orgs.
  if (organisation !== 'Well Life Clinic') {
    keys = keys.filter((key) => key !== 'needsMdtClinician');
  }

  return keys;
};
